import { type ReactNode, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  FluentProvider,
  webLightTheme,
  Text,
  useModalAttributes,
} from '@fluentui/react-components';
import { ArrowSyncRegular, SignOutRegular, PersonAvailableRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { useSessionWarningStyles } from './SessionWarning.styles';

interface SessionWarningProps {
  readonly open: boolean;
  readonly onExtend: () => void;
  readonly onSignOut: () => void;
  readonly onDismissOutside?: () => void;
  readonly onDismissEscape?: () => void;
}

function SessionWarningContent({
  onExtend,
  onSignOut,
  onDismissOutside,
  onDismissEscape,
}: Omit<SessionWarningProps, 'open'>): ReactNode {
  const styles = useSessionWarningStyles();
  const { t } = useTranslation('auth');
  const pointerStartedOnOverlay = useRef(false);
  const extendButtonRef = useRef<HTMLButtonElement>(null);
  const { modalAttributes } = useModalAttributes({ trapFocus: true });

  useEffect(() => {
    extendButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!onDismissEscape) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onDismissEscape();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onDismissEscape]);

  const handleOverlayPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    pointerStartedOnOverlay.current = e.target === e.currentTarget;
  }, []);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (pointerStartedOnOverlay.current && e.target === e.currentTarget && onDismissOutside) {
        onDismissOutside();
      }
      pointerStartedOnOverlay.current = false;
    },
    [onDismissOutside]
  );

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={styles.overlay}
      onPointerDown={handleOverlayPointerDown}
      onClick={handleOverlayClick}
    >
      <div
        className={styles.surface}
        role="dialog"
        aria-modal="true"
        aria-label={t('session.warningTitle')}
        {...modalAttributes}
      >
        <div className={styles.iconContainer}>
          <ArrowSyncRegular className={styles.warningIcon} />
        </div>
        <Text className={styles.title} as="h2" weight="semibold">
          {t('session.warningTitle')}
        </Text>

        <Text id="session-warning-message" className={styles.message} as="p">
          {t('session.warningMessage')}
        </Text>
        <div className={styles.actions}>
          <BasicButton
            variant="secondary"
            icon={<SignOutRegular />}
            onClick={onSignOut}
            className={styles.signOutButton}
          >
            {t('session.signOutButton')}
          </BasicButton>
          <BasicButton
            ref={extendButtonRef}
            variant="primary"
            icon={<PersonAvailableRegular />}
            onClick={onExtend}
            className={styles.extendButton}
            size="medium"
          >
            {t('session.extendButton')}
          </BasicButton>
        </div>
      </div>
    </div>
  );
}

export function SessionWarning({
  open,
  onExtend,
  onSignOut,
  onDismissOutside,
  onDismissEscape,
}: SessionWarningProps): ReactNode {
  if (!open) return null;

  return createPortal(
    <FluentProvider theme={webLightTheme}>
      <SessionWarningContent
        onExtend={onExtend}
        onSignOut={onSignOut}
        onDismissOutside={onDismissOutside}
        onDismissEscape={onDismissEscape}
      />
    </FluentProvider>,
    document.body
  );
}

export default SessionWarning;
